import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import StudentModals from "../studentModals";
import Table from "../../../../core/common/dataTable/index";
import CommonSelect from "../../../../core/common/commonSelect";
import TooltipOption from "../../../../core/common/tooltipOption";
import { StudentResponseType } from "../types/studentResponse";
import { formatDateToYMD } from "../../../../core/utils/dateFormatter";
import { API_CONFIG } from "../../../../core/config";
import ImageWithBasePath from "../../../../core/common/imageWithBasePath";
import { useSelector } from "react-redux";
import { selectUserRole } from "../../../../core/data/slice/authSlice";
import { useCourse } from "../../../configuration/course/hooks/useCourse";
import { useBatchTiming } from "../../../configuration/batch-timing/hooks/useBatchTiming";
import { useSession } from "../../../configuration/session/hooks/useSession";
import { useCenter } from "../../centers/hooks/useCenter";
import { useStudentFilters } from "../hooks/useStudentFilters";
import { useStudent } from "../hooks/useStudent";

const StudentList = () => {
  const routes = all_routes;
  const {
    getStudentState,
    paginationData,
    search,
    pageNumber,
    recordsPerPage,
    filters,
    tempFilters,
    sortConfig,
    handleSearchChange,
    handlePageChange,
    handleFilterChange,
    handleSortChange,
    handleResetFilters,
    handleApplyFilters,
    setTempFilters,
    selectedCourseStages,
  } = useStudentFilters();

  const { students, isLoading, error } = getStudentState();
  const {
    handleActivate,
    handleDeactivate,
    handleDeleteClick,
    handleDeactivateClick,
    statusUpdating,
    selectedStudentId,
    showDeleteModal,
    setShowDeleteModal,
    showDeactivateModal,
    setShowDeactivateModal,
    showActivateModal,
    setShowActivateModal,
    isDeactivateFromDelete,
  } = useStudent();

  const userRole = useSelector(selectUserRole);
  const isCenterAdmin = userRole === "centeradmin";
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);

  const { courses } = useCourse({ size: 100 }).getCourseState();
  const { batchTimings } = useBatchTiming({ size: 100 });
  const { getSessionState } = useSession({ size: 100 });
  const { sessions } = getSessionState();

  const courseOptions = courses.map((course) => ({
    value: course.id.toString(),
    label: course.name,
  }));

  const batchOptions = batchTimings.map((batch) => ({
    value: batch.id.toString(),
    label: `${batch.name} (${batch.start_time} - ${batch.end_time})`,
  }));

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Deactive" },
  ];

  const sessionOptions = sessions.map((session) => ({
    value: session.id.toString(),
    label: session.name,
  }));

  // Update stageOptions to use selectedCourseStages
  const stageOptions = selectedCourseStages.map((stage) => ({
    value: stage.id.toString(),
    label: stage.name,
  }));

  const { getCenterState } = useCenter({
    size: 100,
    skip: userRole !== "superadmin",
  });
  const { centers } = getCenterState();

  const centerOptions = centers.map((center) => ({
    value: center.id.toString(),
    label: center.name,
  }));

  const columns = [
    {
      title: "S. No",
      render: (_: any, __: any, index: number) => index + 1 + (pageNumber - 1) * recordsPerPage,
    },
    {
      title: "Name",
      dataIndex: "first_name",
      render: (text: string, record: StudentResponseType) => (
        <div className="d-flex align-items-center">
          <Link to="#" className="avatar avatar-md">
            {record.docs.photo ? (
              <img
                src={`${API_CONFIG.BASE_URL}students/get-thumbnail/${record.docs.photo
                  }?v=${Date.now()}`}
                className="img-fluid rounded-circle"
                alt={`${record.first_name}${record.last_name ? " " + record.last_name : ""}`}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  const target = e.target as HTMLImageElement;
                  target.src =
                    record.profile.gender?.toLowerCase() === "female"
                      ? "/assets/img/profiles/default-female.png"
                      : "/assets/img/profiles/default-male.png";
                }}
              />
            ) : (
              <ImageWithBasePath
                src={
                  record.profile.gender?.toLowerCase() === "female"
                    ? "assets/img/profiles/default-female.png"
                    : "assets/img/profiles/default-male.png"
                }
                className="img-fluid rounded-circle"
                alt={`${record.first_name}${record.last_name ? " " + record.last_name : ""}`}
              />
            )}
          </Link>
          <div className="ms-2">
            <p className="text-dark mb-0">
              <Link
                to={`${routes.studentDetail.replace(
                  ":id",
                  record.id.toString()
                )}`}
                className="link-primary"
              >
                {`${record.first_name}${record.last_name ? " " + record.last_name : ""}`}
              </Link>
            </p>
          </div>
        </div>
      ),
    },
    {
      title: "Gender",
      dataIndex: ["profile", "gender"],
      render: (_: string, record: StudentResponseType) => record.profile.gender,
    },
    {
      title: "Level",
      dataIndex: ["course", "stage", "name"],
      render: (_: string, record: StudentResponseType) => record.course?.stage?.name || "-",
    },
    ...(isCenterAdmin
      ? []
      : [
        {
          title: "Center",
          dataIndex: ["center", "name"],
          render: (_: string, record: StudentResponseType) =>
            record.center?.name || "-",
        },
      ]),
    {
      title: "Status",
      dataIndex: "status",
      render: (text: string) => (
        <span
          className={`badge badge-soft-${text === "active" ? "success" : "danger"
            } d-inline-flex align-items-center`}
        >
          <i className="ti ti-circle-filled fs-5 me-1"></i>
          {text === "inactive" ? "Deactivated" : text}
        </span>
      ),
    },
    {
      title: "Date of Join",
      dataIndex: "created_at",
      render: (text: string) => formatDateToYMD(text),
    },
    {
      title: "DOB",
      dataIndex: ["profile", "dob"],
      render: (_: string, record: StudentResponseType) =>
        record.profile.dob ? formatDateToYMD(record.profile.dob) : "N/A",
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (text: string, record: StudentResponseType) => (
        <>
          <div className="d-flex align-items-center">
            <Link
              to={`tel:${record.profile.phone}`}
              className="btn btn-outline-light bg-white btn-icon d-flex align-items-center justify-content-center rounded-circle p-0 me-2"
            >
              <i className="ti ti-phone" />
            </Link>
            <Link
              to={`mailto:${record.profile.email}`}
              className="btn btn-outline-light bg-white btn-icon d-flex align-items-center justify-content-center rounded-circle p-0 me-3"
            >
              <i className="ti ti-mail" />
            </Link>
            {record.status === "active" ? (
              <>
                <div className="dropdown">
                  <Link
                    to="#"
                    className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <i className="ti ti-dots-vertical fs-14" />
                  </Link>
                  <ul className="dropdown-menu dropdown-menu-right p-3">
                    <li>
                      <Link
                        className="dropdown-item rounded-1"
                        to={`${routes.studentDocuments.replace(
                          ":id",
                          record.id.toString()
                        )}`}
                      >
                        <i className="ti ti-file-description me-2" />
                        Upload Documents
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="dropdown-item rounded-1"
                        to={`${routes.studentDetail.replace(
                          ":id",
                          record.id.toString()
                        )}`}
                      >
                        <i className="ti ti-menu me-2" />
                        View Student
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="dropdown-item rounded-1"
                        to={`${routes.generateStudentID.replace(
                          ":id",
                          record.id.toString()
                        )}`}
                      >
                        <i className="ti ti-id-badge me-2" />
                        Generate ID
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="dropdown-item rounded-1"
                        to={`${routes.billingSection.replace(
                          ":id",
                          record.id.toString()
                        )}`}
                      >
                        <i className="ti ti-calculator me-2" />
                        Billing Section
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="dropdown-item rounded-1"
                        to={`${routes.editStudent.replace(
                          ":id",
                          record.id.toString()
                        )}`}
                      >
                        <i className="ti ti-edit me-2" />
                        Edit
                      </Link>
                    </li>
                    {userRole !== "superadmin" && (
                      <li>
                        <Link
                          className="dropdown-item rounded-1"
                          to={`${routes.addBookRequest}?student_id=${record.id}&course_id=${record.course?.id}&stage_id=${record.course?.stage?.id}`}
                        >
                          <i className="ti ti-book me-2" />
                          Request Book
                        </Link>
                      </li>
                    )}
                    <li>
                      <button
                        className="dropdown-item rounded-1"
                        onClick={() => handleDeactivateClick(record.id)}
                        disabled={statusUpdating[record.id]}
                      >
                        <i className="ti ti-user-off me-2" />
                        {statusUpdating[record.id] ? (
                          <span>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Updating...
                          </span>
                        ) : (
                          "Deactivate Student"
                        )}
                      </button>
                    </li>
                    <li>
                      <button
                        className="dropdown-item rounded-1"
                        onClick={() => handleDeleteClick(record.id)}
                      >
                        <i className="ti ti-trash me-2" />
                        Delete
                      </button>
                    </li>
                  </ul>
                </div>
              </>
            ) : (
              <div className="dropdown">
                <Link
                  to="#"
                  className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <i className="ti ti-dots-vertical fs-14" />
                </Link>
                <ul className="dropdown-menu dropdown-menu-right p-3">
                  <li>
                    <button
                      className="dropdown-item rounded-1"
                      onClick={() => handleActivate(record.id)}
                      disabled={statusUpdating[record.id]}
                    >
                      <i className="ti ti-refresh me-2" />
                      {statusUpdating[record.id] ? (
                        <span>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Updating...
                        </span>
                      ) : (
                        "Activate Student"
                      )}
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </>
      ),
    },
  ];

  // Add this function to sync tempFilters with filters
  const syncTempFilters = () => {
    setTempFilters(filters);
  };

  // Add these helper functions at the top of the component
  const findOptionByValue = (options: any[], value: string | undefined) => {
    if (!value) return undefined;
    return options.find((option) => option.value === value);
  };

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Student List</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">Students</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Student List
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <TooltipOption />
              <div className="mb-2">
                <Link
                  to={routes.addStudent}
                  className="btn btn-primary d-flex align-items-center"
                >
                  <i className="ti ti-square-rounded-plus me-2" />
                  Add Student
                </Link>
              </div>
            </div>
          </div>
          {/* /Page Header */}
          {/* Students List */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Student List</h4>
              <div className="d-flex align-items-center flex-wrap">
                <div className="dropdown mb-3 me-2">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                    onClick={syncTempFilters}
                  >
                    <i className="ti ti-filter me-2" />
                    Filter
                  </Link>
                  <div
                    className="dropdown-menu drop-width"
                    ref={dropdownMenuRef}
                  >
                    <form>
                      <div className="d-flex align-items-center border-bottom p-3">
                        <h4>Filter</h4>
                      </div>
                      <div className="p-3 pb-0 border-bottom">
                        <div className="row">
                          {userRole === "superadmin" && (
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">Center</label>
                                <CommonSelect
                                  className="select"
                                  options={centerOptions}
                                  value={findOptionByValue(
                                    centerOptions,
                                    tempFilters.center_id?.toString()
                                  )}
                                  onChange={(value) =>
                                    handleFilterChange("center_id", value)
                                  }
                                />
                              </div>
                            </div>
                          )}
                          <>
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">Session</label>
                                <CommonSelect
                                  className="select"
                                  options={sessionOptions}
                                  value={findOptionByValue(
                                    sessionOptions,
                                    tempFilters.session_id?.toString()
                                  )}
                                  onChange={(value) =>
                                    handleFilterChange("session_id", value)
                                  }
                                />
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">Course</label>
                                <CommonSelect
                                  className="select"
                                  options={courseOptions}
                                  value={findOptionByValue(
                                    courseOptions,
                                    tempFilters.course_id?.toString()
                                  )}
                                  onChange={(value) => {
                                    handleFilterChange(
                                      "course_id",
                                      value,
                                      courses
                                    );
                                  }}
                                />
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">Level</label>
                                <CommonSelect
                                  className="select"
                                  options={stageOptions}
                                  value={findOptionByValue(
                                    stageOptions,
                                    tempFilters.stage_id?.toString()
                                  )}
                                  onChange={(value) =>
                                    handleFilterChange("stage_id", value)
                                  }
                                  isDisabled={!tempFilters.course_id}
                                />
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">Batch</label>
                                <CommonSelect
                                  className="select"
                                  options={batchOptions}
                                  value={findOptionByValue(
                                    batchOptions,
                                    tempFilters.batch_id?.toString()
                                  )}
                                  onChange={(value) =>
                                    handleFilterChange("batch_id", value)
                                  }
                                />
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">Status</label>
                                <CommonSelect
                                  className="select"
                                  options={statusOptions}
                                  value={findOptionByValue(
                                    statusOptions,
                                    tempFilters.status
                                  )}
                                  onChange={(value) =>
                                    handleFilterChange("status", value)
                                  }
                                />
                              </div>
                            </div>
                          </>
                        </div>
                      </div>
                      <div className="p-3 d-flex align-items-center justify-content-end">
                        <button
                          type="button"
                          className="btn btn-light me-3"
                          onClick={handleResetFilters}
                        >
                          Reset
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleApplyFilters}
                        >
                          Apply
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
                <div className="d-flex align-items-center bg-white border rounded-2 p-1 mb-3 me-2">
                  <Link
                    to={routes.studentList}
                    className="active btn btn-icon btn-sm me-1 primary-hover"
                  >
                    <i className="ti ti-list-tree" />
                  </Link>
                  <Link
                    to={routes.studentGrid}
                    className="btn btn-icon btn-sm bg-light primary-hover"
                  >
                    <i className="ti ti-grid-dots" />
                  </Link>
                </div>
                <div className="d-flex align-items-center flex-wrap mb-3">
                  <div className="dropdown me-2">
                    <Link
                      to="#"
                      className="btn btn-white border d-flex align-items-center"
                      data-bs-toggle="dropdown"
                    >
                      <i className="ti ti-arrows-sort me-1 text-muted" />
                      {sortConfig.sort_by ? (
                        <span>
                          <span className="text-muted">Sort By:</span>{" "}
                          <span className="text-dark fw-medium">
                            {sortConfig.sort_by}
                          </span>
                          <i
                            className={`ms-1 ti ti-arrow-${sortConfig.sort_order === "asc" ? "up" : "down"
                              } text-muted`}
                          />
                        </span>
                      ) : (
                        <span className="text-muted">Sort By</span>
                      )}
                    </Link>
                    <div
                      className="dropdown-menu shadow border-0"
                      style={{ minWidth: "280px" }}
                    >
                      <div className="p-3">
                        <h6 className="mb-0">
                          <i className="ti ti-arrows-sort me-2 text-muted"></i>
                          <span className="text-muted">Sort By Field</span>
                        </h6>
                      </div>

                      <div className="p-3">
                        <div className="mb-3">
                          <label className="form-label small">Sort Field</label>
                          <select
                            className="form-select"
                            value={sortConfig.sort_by || ""}
                            onChange={(e) => {
                              if (e.target.value) {
                                handleSortChange(
                                  e.target.value,
                                  sortConfig.sort_order || "asc"
                                );
                              } else {
                                handleSortChange("", "asc");
                              }
                            }}
                          >
                            <option value="">Select field</option>
                            <option value="name">Student Name</option>
                            {!isCenterAdmin && (
                              <option value="center_name">Center</option>
                            )}
                          </select>
                        </div>

                        {sortConfig.sort_by && (
                          <div className="mb-3">
                            <label className="form-label small">
                              Sort Direction
                            </label>
                            <div className="btn-group w-100">
                              <button
                                type="button"
                                className={`btn ${sortConfig.sort_order === "asc"
                                  ? "btn-primary"
                                  : "btn-outline-primary"
                                  }`}
                                onClick={() =>
                                  sortConfig.sort_by
                                    ? handleSortChange(
                                      sortConfig.sort_by,
                                      "asc"
                                    )
                                    : null
                                }
                              >
                                <i className="ti ti-sort-ascending me-1"></i>
                                Ascending
                              </button>
                              <button
                                type="button"
                                className={`btn ${sortConfig.sort_order === "desc"
                                  ? "btn-primary"
                                  : "btn-outline-primary"
                                  }`}
                                onClick={() =>
                                  sortConfig.sort_by
                                    ? handleSortChange(
                                      sortConfig.sort_by,
                                      "desc"
                                    )
                                    : null
                                }
                              >
                                <i className="ti ti-sort-descending me-1"></i>
                                Descending
                              </button>
                            </div>
                          </div>
                        )}

                        {sortConfig.sort_by && (
                          <div>
                            <button
                              type="button"
                              className="btn btn-light btn-sm w-100 d-flex align-items-center justify-content-center"
                              onClick={() => handleSortChange("", "asc")}
                            >
                              <i className="ti ti-x me-1"></i>
                              Clear Sort
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-body p-0 py-3">
              {isLoading ? (
                <div className="text-center">Loading students...</div>
              ) : error ? (
                <div className="alert alert-danger m-3">
                  Error loading students: {error.toString()}
                </div>
              ) : (
                <Table
                  dataSource={students}
                  columns={columns}
                  Selection={false}
                  onSearchChange={handleSearchChange}
                  pagination={{
                    current: pageNumber || 1,
                    pageSize: recordsPerPage || 10,
                    total: paginationData.totalItems || 0,
                    onChange: handlePageChange || (() => { }),
                    pageSizeOptions: [10, 20, 30],
                  }}
                />
              )}
            </div>
          </div>
          {/* /Students List */}
        </div>
      </div>
      {/* /Page Wrapper */}
      <StudentModals
        StudentId={selectedStudentId}
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        showDeactivateModal={showDeactivateModal}
        setShowDeactivateModal={setShowDeactivateModal}
        showActivateModal={showActivateModal}
        setShowActivateModal={setShowActivateModal}
        isDeactivateFromDelete={isDeactivateFromDelete}
      />
    </>
  );
};

export default StudentList;
